// The signal bus (P0.7) — the two ends of Architecture §2's signal ring.
//
// The audio thread publishes one frame per render quantum; the render worker and the UI read
// them. Overwrite-oldest, because the producer runs at 375 Hz and every consumer is slower: a
// meter and a scene both want the newest frame, never a backlog.
//
// In practice a consumer that drains each redraw never drops anything — ~6 frames accumulate per
// 60 Hz frame against 64 slots, measured at 0 dropped in a live browser. The policy is not for
// the steady state; it is for the stall. A 600 ms freeze of the reading thread lapped the ring
// three times over and dropped 147 frames, and the audio thread neither noticed nor waited. That
// is R-1's mitigation, and the reason the writer never checks the reader (P0.3).
//
// Why both ends live here rather than the reader sitting in /visual/core, where Architecture §11
// lists it:
//
//   * The worklet must import the publisher, and `worklet-isolation` allows /platform/sab.
//     /audio/engine and /visual/core are both off-limits to it.
//   * The reader has two consumers already — the render worker (P0.8, scenes) and the main
//     thread (P0.7, meters). Byte offsets duplicated across two modules is exactly the mistake
//     P0.2 exists to prevent.
//   * Keeping the pair together means they can be round-tripped in a Node test, which is the
//     only way to check that the side that writes and the side that reads agree. Neither end is
//     testable against the real thing otherwise.
//
// Architecture §11's "/visual/core — signal reader" still holds: that is the render worker's use
// of this, with the scene-facing shape FR-26 describes. This is the decode both share.

import { OverwriteRingReader, OverwriteRingWriter } from './spsc-ring';
import type { SignalSourceIndex } from '../types/rings';
import {
  SIGNAL_FRAME,
  SIGNAL_FRAME_BYTES,
  SIGNAL_RING,
  SIGNAL_SOURCE,
  SIGNAL_SOURCE_COUNT,
  SIGNAL_SOURCE_RECORD,
  SIGNAL_SOURCE_RECORD_BYTES,
  signalSourceByteOffset,
} from '../types/rings';

/** Nothing has opened a frame yet. `currentFrame` starts at 0, so -1 can never collide with it. */
const NO_QUANTUM = -1;

/**
 * One `readFramePeaks` reading — every meter number the UI needs, from one pass over the ring.
 *
 * Caller-owned and reused forever, which is the whole reason it is a struct of typed arrays rather
 * than a returned object: this is filled 60 times a second on the main thread, and Stack §2's rule
 * about SAB-reading paths is as much about garbage as it is about React.
 *
 * Indexed by `SignalSourceIndex` throughout, so `peak[SIGNAL_SOURCE.STRIP_A]` reads the way it
 * looks.
 */
export interface MeterFrame {
  /** Loudest absolute sample per source across the interval, linear. */
  readonly peak: Float32Array;
  /** Loudest block RMS per source across the interval, linear. */
  readonly rms: Float32Array;
  /** Newest playhead per source, in seconds — latest-wins, not a maximum. */
  readonly playheadSeconds: Float32Array;
  /** **Lowest** master limiter gain across the interval, linear. 1 is untouched. */
  gainReduction: number;
  /** How many frames were folded in. 0 means nothing new arrived and the rest is stale. */
  frames: number;
}

/** A zeroed `MeterFrame`, sized to the current source count. Allocate once, outside any loop. */
export function createMeterFrame(): MeterFrame {
  return {
    peak: new Float32Array(SIGNAL_SOURCE_COUNT),
    rms: new Float32Array(SIGNAL_SOURCE_COUNT),
    playheadSeconds: new Float32Array(SIGNAL_SOURCE_COUNT),
    gainReduction: 1,
    frames: 0,
  };
}

/** Byte offset of the master RMS field within a frame. Resolved once, not per block. */
const MASTER_RMS_OFFSET =
  signalSourceByteOffset(SIGNAL_SOURCE.MASTER) + SIGNAL_SOURCE_RECORD.RMS;

/**
 * The audio thread's end. One frame per render quantum.
 *
 * **The API changed shape at P2.9, and P0.7 predicted the moment: "`SignalPublisher.publish` grows
 * a parameter list or an options object when they land; worth choosing then rather than now."** The
 * choice was between three shapes, and both of the ones P0.7 named are wrong:
 *
 *   * *A longer parameter list* — `publish(timestamp, masterRms, deckARms, deckAPlayhead, …)`. FR-26
 *     has ten fields across three sources. That is a thirty-argument function whose call sites are
 *     unreadable and whose arguments are positionally interchangeable floats, which is the single
 *     easiest kind of bug to write and the hardest to see.
 *   * *An options object* — `publish({ master: { rms }, deckA: { rms, playhead } })`. Readable, and
 *     it **allocates on the audio thread**, which the Playbook forbids outright ("real-time paths
 *     never allocate"). V8 might escape-analyse a small literal away; relying on that for a nested
 *     thirty-field object is a bet on an optimisation, not a design.
 *   * *Staged writes* — `begin(timestamp)`, then `write(...)` per field, then `commit()`. Chosen.
 *
 * Staging costs nothing and scales to the whole of FR-26: `begin` claims the slot, each `write`
 * lands a float directly in the SAB at a computed offset, `commit` publishes. No arguments to
 * transpose, no object to allocate, and — the part that matters most as sources multiply — **the
 * code that computes a signal is the code that writes it.** The deck writes its own block from the
 * deck processor; the master writes its own; neither has to be threaded through a call that knows
 * about both.
 */
export class SignalPublisher {
  readonly #writer: OverwriteRingWriter;
  readonly #doubles: Float64Array;
  readonly #uints: Uint32Array;
  readonly #floats: Float32Array;

  /** int32 bit pattern, like the rings' own sequences — a uint32 past 2^31 would allocate. */
  #sequence = 0;

  /**
   * The slot currently open for writing, or -1 between frames.
   *
   * A field rather than a parameter passed back to the caller, because the caller is a per-block
   * audio path and handing it an offset to carry around is handing it a way to write into the wrong
   * slot. `write` before `begin` is a no-op rather than a throw: throwing on the audio thread kills
   * the whole graph, which deck-processor.ts already refuses to do.
   */
  #openOffset = -1;

  /**
   * The render quantum the open frame describes, or `NO_QUANTUM` before the first one (P3.7).
   *
   * Only `openQuantum` reads it. `begin`/`commit` leave it alone, so a caller driving the publisher
   * by hand — a test, or the bench's synthetic waveform feed — is unaffected by any of this.
   */
  #quantum = NO_QUANTUM;

  constructor(sab: SharedArrayBuffer) {
    this.#writer = new OverwriteRingWriter(sab, SIGNAL_RING);
    this.#doubles = new Float64Array(sab);
    this.#uints = new Uint32Array(sab);
    this.#floats = new Float32Array(sab);
  }

  /**
   * Joins the frame for one render quantum, opening it if this is the first writer to arrive
   * (P3.7).
   *
   * **This exists because P3.7 gives the ring a second producer and it was built for one.** Both
   * decks write into one signal ring — Architecture §2's ring, and P3.7's rule: "the ring's frame
   * carries every source precisely so a deck-A and a deck-B reading describe the same block". With
   * `begin`/`commit` per deck that is impossible: each deck would claim its *own* slot, publish a
   * frame carrying its own block and whatever bytes the slot held 64 frames ago in the other's, and
   * `OverwriteRingWriter` caches its write sequence in a field, so two writers over one buffer would
   * also disagree about where the ring had got to. A meter reading and a playhead that describe
   * different blocks is precisely the incoherence the frame exists to prevent.
   *
   * So the frame belongs to the *quantum*, not to a deck. Both processors run in one
   * AudioWorkletGlobalScope on one thread, so they can share a publisher (`sharedSignalPublisher`)
   * and both hand it the same `currentFrame`: the first to arrive opens the frame, the second joins
   * it, each writes its own source block, and nobody commits.
   *
   * **The commit is deferred to the next quantum, deliberately.** Committing at the end of a block
   * would need to know how many decks are going to write in it, and that number is unknowable: a
   * deck between node rebuilds is registered and silent, and a processor has no destructor to
   * deregister with. Waiting for the *next* block instead depends on nothing but the audio thread
   * still running, which is the one thing that is always true while there is anything to publish.
   * The cost is one render quantum of delay — 2.7 ms at 48 kHz — against consumers that redraw at
   * 60 Hz. The last frame before the context stops is never published, which is the honest outcome
   * for a block after which nothing happened.
   *
   * The frame is **cleared** as it opens, so a deck that does not write this quantum reads as zero
   * rather than as its own reading from 64 frames ago. Silence is the truth about a deck with no
   * node; a stale level 170 ms old is not, and it would flicker as the ring wrapped.
   */
  openQuantum(quantum: number, timestamp: number): void {
    if (quantum === this.#quantum) {
      return;
    }
    // The previous quantum's frame is complete — every writer that was going to write in it has
    // been and gone, because they all run inside one block.
    this.commit();
    this.#quantum = quantum;
    this.begin(timestamp);
    this.#clear();
  }

  /**
   * Zeroes everything in the open frame that a writer might not fill.
   *
   * Not the timestamp or the sequence: `begin` has just written both. An explicit loop rather than
   * `fill` on a subarray, because a subarray allocates and this runs on the audio thread —
   * 62 stores per block at 375 Hz (was 37 before P4.7 added two sources and a header field), once
   * per quantum rather than once per writer.
   *
   * ⚠️ **Gain reduction is cleared to 1, not 0, and it is the one field here where zero is a lie.**
   * Every other field is a level, and a level nobody wrote is honestly silence. This one is a
   * *gain*: 0 would read as "the limiter removed everything", which is the most alarming possible
   * reading, published on precisely the blocks where the mixer did not run. 1 is "untouched", which
   * is what a quantum with no limiter in it actually means.
   */
  #clear(): void {
    const offset = this.#openOffset;
    if (offset < 0) {
      return;
    }
    this.#uints[(offset + SIGNAL_FRAME.ONSET_FLAGS) >>> 2] = 0;
    this.#floats[(offset + SIGNAL_FRAME.GAIN_REDUCTION) >>> 2] = 1;
    const from = (offset + SIGNAL_FRAME.SOURCES) >>> 2;
    const to = (offset + SIGNAL_FRAME_BYTES) >>> 2;
    for (let i = from; i < to; i += 1) {
      this.#floats[i] = 0;
    }
  }

  /** Opens a frame. Never blocks and never refuses — a stalled renderer must not be able to reach
   * back and stall the audio thread, which is R-1's whole mitigation. */
  begin(timestamp: number): void {
    const offset = this.#writer.claim();
    this.#openOffset = offset;
    this.#doubles[offset >>> 3] = timestamp;
    this.#uints[(offset + SIGNAL_FRAME.SEQUENCE) >>> 2] = this.#sequence;
  }

  /**
   * Writes one field of one source into the open frame.
   *
   * `field` is a `SIGNAL_SOURCE_RECORD` offset. Both arguments are compile-time constants at every
   * real call site, so the two additions fold away — this is a single indexed store in practice.
   */
  write(source: SignalSourceIndex, field: number, value: number): void {
    if (this.#openOffset < 0) {
      return;
    }
    this.#floats[(this.#openOffset + signalSourceByteOffset(source) + field) >>> 2] =
      value;
  }

  /**
   * Writes the master limiter's gain reduction into the open frame's header (P4.7).
   *
   * Its own method rather than a `write` with a source, because it has no source: see
   * `SIGNAL_FRAME.GAIN_REDUCTION` for why one limiter on one bus does not belong in a per-source
   * record. A linear gain — 1 is untouched.
   */
  writeGainReduction(gain: number): void {
    if (this.#openOffset < 0) {
      return;
    }
    this.#floats[(this.#openOffset + SIGNAL_FRAME.GAIN_REDUCTION) >>> 2] = gain;
  }

  /** Publishes the frame. Every `write` above must land first — P0.3's rings rest on exactly that
   * ordering, and `commit` is the release. */
  commit(): void {
    if (this.#openOffset < 0) {
      return;
    }
    this.#writer.commit();
    this.#sequence = (this.#sequence + 1) | 0;
    this.#openOffset = -1;
    // Forgotten too, so a caller mixing `openQuantum` with a manual `commit` cannot leave the
    // publisher believing a quantum is still open over a slot it has already published — every
    // `write` after that would silently land nowhere.
    this.#quantum = NO_QUANTUM;
  }
}

/**
 * One publisher per signal ring, per realm (P3.7).
 *
 * **The registry is what makes two decks one producer.** `openQuantum`'s frame-per-block only works
 * if both decks are talking to the *same* publisher object: the open slot and the write sequence
 * are its fields, and two publishers over one ring would each keep their own copy of both — which
 * is two writers on a ring built for one, wearing a disguise. Both `DeckProcessor`s are constructed
 * in one `AudioWorkletGlobalScope`, so a module-level map in the worklet's bundle is exactly the
 * scope this needs: no atomics, no cross-thread coordination, because there is no second thread.
 *
 * ⚠️ **Keyed by an id, and it was keyed by the `SharedArrayBuffer` first. That version was wrong,
 * every unit test passed, and the app was visibly broken.**
 *
 * `processorOptions` is **structured-cloned** on its way to the audio thread. A cloned
 * `SharedArrayBuffer` shares its memory — that is the whole point of it — but it is a *different
 * JS object* in the receiving realm. So deck A's processor and deck B's processor were handed two
 * distinct objects over one ring, a `WeakMap` keyed on identity missed, and each got a publisher of
 * its own. Every frame then carried one deck's block and a cleared zero where the other's belonged.
 * In Node both sides hold the same object, so the round-trip test was green throughout; what caught
 * it was a browser, in ten seconds, exactly as R-B says.
 *
 * An id survives the clone because it is a number. It is minted per ring by whoever creates the
 * ring (`AudioGraph`), which keeps the identity "the ring" rather than "the module" — FR-19's cue
 * context at M5 is a second graph with a second ring, and it gets its own publisher rather than
 * fighting a global.
 *
 * Every realm has its own map, which is the correct reading: coordination is only needed among
 * writers, and every writer lives on the audio thread.
 */
const PUBLISHERS = new Map<number, SignalPublisher>();

export function sharedSignalPublisher(
  /** `AudioGraph.signalsId` — minted with the ring and carried in `processorOptions`. */
  signalsId: number,
  signals: SharedArrayBuffer,
): SignalPublisher {
  const existing = PUBLISHERS.get(signalsId);
  if (existing !== undefined) {
    return existing;
  }
  const created = new SignalPublisher(signals);
  PUBLISHERS.set(signalsId, created);
  return created;
}

/**
 * A consumer's end. Copies a frame out, then exposes its fields.
 *
 * The copy is not laziness — an overwrite writer will rewrite the slot being read, so reading in
 * place would hand out a frame half from one block and half from another. `OverwriteRingReader`
 * copies and then checks whether it was lapped mid-copy (P0.3).
 */
export class SignalReader {
  readonly #reader: OverwriteRingReader;

  // The frame is copied into this, once allocated, forever reused. A reader that allocated per
  // frame would allocate 60 times a second on the main thread and 375 in a worker.
  readonly #bytes: Uint8Array;
  readonly #doubles: Float64Array;
  readonly #uints: Uint32Array;
  readonly #floats: Float32Array;

  constructor(sab: SharedArrayBuffer) {
    this.#reader = new OverwriteRingReader(sab, SIGNAL_RING);
    this.#bytes = new Uint8Array(SIGNAL_FRAME_BYTES);
    this.#doubles = new Float64Array(this.#bytes.buffer);
    this.#uints = new Uint32Array(this.#bytes.buffer);
    this.#floats = new Float32Array(this.#bytes.buffer);
  }

  /** Copies the next published frame in. False when there is nothing new. */
  read(): boolean {
    return this.#reader.read(this.#bytes);
  }

  /** AudioContext time of the block this frame describes. */
  get timestamp(): number {
    return this.#doubles[SIGNAL_FRAME.TIMESTAMP >>> 3] ?? 0;
  }

  /** The producing block's sequence — gaps here are frames this reader was too slow for. */
  get sequence(): number {
    return this.#uints[SIGNAL_FRAME.SEQUENCE >>> 2] ?? 0;
  }

  /** The master limiter's gain for this frame, linear. 1 is untouched (P4.7). */
  get gainReduction(): number {
    return this.#floats[SIGNAL_FRAME.GAIN_REDUCTION >>> 2] ?? 1;
  }

  get masterRms(): number {
    return this.#floats[MASTER_RMS_OFFSET >>> 2] ?? 0;
  }

  /**
   * The loudest master RMS across every frame published since the last call, or null if none.
   *
   * Peak-across-the-interval, not "the latest frame", and the difference matters. At 60 Hz a
   * consumer sees roughly six frames per redraw; taking only the last would silently discard five
   * out of six, and a short transient landing in one of them would never reach a meter. That is
   * the classic reason a meter misses the very peaks it exists to show.
   */
  readMasterPeak(): number | null {
    let peak = -1;

    while (this.read()) {
      const rms = this.masterRms;
      if (rms > peak) {
        peak = rms;
      }
    }

    return peak < 0 ? null : peak;
  }

  /** One field of one source, from the frame currently copied in. */
  readSource(source: SignalSourceIndex, field: number): number {
    return this.#floats[(signalSourceByteOffset(source) + field) >>> 2] ?? 0;
  }

  /**
   * The loudest RMS for one source across every frame since the last call, or null if none.
   *
   * The generalisation of `readMasterPeak`, and for the same reason it exists: at 60 Hz a consumer
   * sees ~6 frames per redraw, so taking only the newest silently discards five out of six and a
   * short transient landing in one of them never reaches a meter.
   */
  readSourcePeak(source: SignalSourceIndex): number | null {
    let peak = -1;
    while (this.read()) {
      const rms = this.readSource(source, SIGNAL_SOURCE_RECORD.RMS);
      if (rms > peak) {
        peak = rms;
      }
    }
    return peak < 0 ? null : peak;
  }

  /**
   * The **newest** playhead for a source, in seconds, or null if no frame has arrived.
   *
   * Latest-wins rather than peak, because a position is not a level: the most recent one is simply
   * the true one, and taking a maximum across an interval would make the playhead jump ahead of the
   * audio whenever a frame arrived late. Drains the ring, so a caller wanting both a level and a
   * position must use `readFramePeaks` instead of calling two draining readers.
   */
  readLatestPlayhead(source: SignalSourceIndex): number | null {
    let latest: number | null = null;
    while (this.read()) {
      latest = this.readSource(source, SIGNAL_SOURCE_RECORD.PLAYHEAD_SECONDS);
    }
    return latest;
  }

  /**
   * Folds **every** frame published since the last call into one reading — every source's peak and
   * RMS, every source's playhead, and the limiter's gain reduction (P4.7).
   *
   * ⚠️ **This is the method the draining readers above have pointed at since P2.9, and it did not
   * exist.** `readLatestPlayhead`'s comment says "a caller wanting both a level and a position must
   * use `readFramePeaks` instead of calling two draining readers" — correct advice about a method
   * nobody had written. It matters more at P4.7 than it did then, because a meter panel wants seven
   * numbers at once and `read()` advances the cursor: call `readSourcePeak(STRIP_A)` and then
   * `readSourcePeak(STRIP_B)` and the first drains the ring, so the second returns null. Not a
   * hypothetical — it is the obvious way to write the panel.
   *
   * **One pass, one cursor, every number from the same set of frames.** That is P4.7's "one frame,
   * not three readings" carried to the consumer: the rule is worth nothing if the audio thread
   * publishes coherently and the UI then samples the ring three times.
   *
   * ⚠️ **Gain reduction accumulates by `min`, everything else by `max`.** It is a gain, not a
   * level: the block where the limiter worked hardest is the block with the *lowest* number, and a
   * max across the interval would report the calmest block in it — a gain-reduction meter that
   * reads zero precisely when the limiter is working. Playheads take the newest rather than either,
   * for `readLatestPlayhead`'s reason: a position is not a level, and a max would let the playhead
   * run ahead of the audio whenever a frame arrived late.
   *
   * Fills a caller-owned `MeterFrame` and allocates nothing — this runs once per animation frame on
   * the main thread, where 60 allocations a second is exactly the garbage Stack §2 keeps off this
   * path. Returns false when no frame arrived, leaving `out` untouched so a caller can hold the
   * previous reading rather than fall to silence on a single missed vsync.
   */
  readFramePeaks(out: MeterFrame): boolean {
    let frames = 0;

    while (this.read()) {
      if (frames === 0) {
        out.peak.fill(0);
        out.rms.fill(0);
        out.gainReduction = 1;
      }
      frames += 1;

      for (let source = 0; source < SIGNAL_SOURCE_COUNT; source += 1) {
        const base = (SIGNAL_FRAME.SOURCES + source * SIGNAL_SOURCE_RECORD_BYTES) >>> 2;
        const peak = this.#floats[base + (SIGNAL_SOURCE_RECORD.PEAK >>> 2)] ?? 0;
        const rms = this.#floats[base + (SIGNAL_SOURCE_RECORD.RMS >>> 2)] ?? 0;
        if (peak > (out.peak[source] ?? 0)) {
          out.peak[source] = peak;
        }
        if (rms > (out.rms[source] ?? 0)) {
          out.rms[source] = rms;
        }
        // Latest-wins, so this is an unconditional store rather than a comparison.
        out.playheadSeconds[source] =
          this.#floats[base + (SIGNAL_SOURCE_RECORD.PLAYHEAD_SECONDS >>> 2)] ?? 0;
      }

      const gain = this.gainReduction;
      if (gain < out.gainReduction) {
        out.gainReduction = gain;
      }
    }

    out.frames = frames;
    return frames > 0;
  }

  /** Frames the writer overwrote before this reader reached them. Expected, and worth watching. */
  get dropped(): number {
    return this.#reader.dropped;
  }
}
